import { INote, INoteLikeSummary } from "notes/models";
import { PaginatedResult } from "shared/models";
import { http } from "shared/api/http";

const buildNotesPath = (tenantId: string, organizationId: string) =>
  `${process.env.REACT_APP_API_BASE_URL}/api/v1/tenants/${tenantId}/organizations/${organizationId}`;

export const createNote = async (
  tenantId: string,
  organizationId: string,
  projectId: string,
  note: { content: string; mentionIds?: string[] }
) => {
  if (!tenantId || !organizationId || !projectId) {
    throw new Error("tenantId, organizationId, and projectId are required to create a note");
  }

  const basePath = buildNotesPath(tenantId, organizationId);
  const encodedContent = btoa(encodeURIComponent(note.content));
  const res = await http.post<INote>(
    `${basePath}/notes`,
    { ...note, content: encodedContent, contentEncoded: true, projectId }
  );
  return res?.data;
};

export const dismissNote = async (
  tenantId: string,
  organizationId: string,
  noteId: string
) => {
  const basePath = buildNotesPath(tenantId, organizationId);
  const res = await http.post(`${basePath}/notes/${noteId}/dismiss`);
  return res?.data;
};

/**
 * Like and unlike are separate idempotent calls rather than one toggle endpoint
 * so a double-tap or a retry can't land the server on the opposite state from
 * the one the button is showing.
 */
export const likeNote = async (
  tenantId: string,
  organizationId: string,
  noteId: string
) => {
  const basePath = buildNotesPath(tenantId, organizationId);
  const res = await http.post<INoteLikeSummary>(`${basePath}/notes/${noteId}/like`);
  return res?.data;
};

export const unlikeNote = async (
  tenantId: string,
  organizationId: string,
  noteId: string
) => {
  const basePath = buildNotesPath(tenantId, organizationId);
  const res = await http.delete<INoteLikeSummary>(`${basePath}/notes/${noteId}/like`);
  return res?.data;
};

export const getNotes = async (
  tenantId: string,
  organizationId: string,
  projectId?: string,
  lastEvaluatedKey?: any
) => {
  if (!tenantId || !organizationId) {
    throw new Error("tenantId and organizationId are required to fetch notes");
  }

  const params = lastEvaluatedKey ? { cursor: lastEvaluatedKey } : {};
  const basePath = buildNotesPath(tenantId, organizationId);

  if (projectId) {
    // Get notes for a specific project
    const res = await http.get<PaginatedResult<INote>>(
      `${basePath}/projects/${projectId}/notes`,
      { params }
    );
    return res?.data;
  } else {
    // Get notes where current user is mentioned
    const res = await http.get<PaginatedResult<INote>>(
      `${basePath}/notes/mentions`,
      { params }
    );
    return res?.data;
  }
};
